<div class="content-container margin-bottom">
  <!-- WELCOME MESSAGE -->
  <mat-card 
    *ngIf="! (sessionHideHelper ?? prefHideHelper ?? !arePreferencesLoaded)"
    appearance="outlined"
    class="timesheet-status"
  >
    <mat-card-header>
      <mat-card-title>Welcome to TKS!</mat-card-title>
    </mat-card-header>
    <mat-card-content>
      <mat-list>
        <mat-list-item>- The timesheet operates similar to well-known spreadsheet applications with automatic saving.</mat-list-item>
        <mat-list-item>- Thus you can navigate with Arrow Keys, Shift/Tab, and Enter or F2 (focus content).</mat-list-item>
        <mat-list-item>- To log hours, enter a cell and type a number (0.0 - 24.0). You can use tenths (e.g., 0.1 = 6 minutes), though for many groups it's not necessary.</mat-list-item>
        <mat-list-item>- You can pin your active projects to the top by hovering and clicking the pin icon, or pressing 'p' when a project row is focused.</mat-list-item>
        <mat-list-item>- To add notes or start a timer, right-click a cell or press 'n' or 't' while it's focused.</mat-list-item>
        <mat-list-item>- <i><b>Hint: </b>For better comfort or accessibility, adjust your settings on the Settings tab.</i></mat-list-item>
      </mat-list>
      Need more help? 
          <a href="https://its.uiowa.edu/services/time-keeping-system" title="TKS documentation page." target="_blank">Visit our documentation page</a>.
      <mat-card-actions>
        <mat-checkbox 
          #greetingDone
          class="margin-right">
          Don't show this again.
        </mat-checkbox>
        <button 
          mat-stroked-button 
          mat-button 
          (click)="dismissGreeting(greetingDone.checked)"
          class="material-symbols-outlined">          
          <mat-icon>close</mat-icon>
            Dismiss
        </button>
      </mat-card-actions>
    </mat-card-content>
  </mat-card>

  <!-- TIMESHEET CONTROLS -->
  <section role="region" aria-label="Timesheet controls" class="btn-actions margin-lg-top margin-bottom">
    <h2 class="visually-hidden">Timesheet controls</h2>
    <button mat-flat-button color="primary" (click)="saveWeek()" [disabled]="isReadonly">
      <mat-icon class="material-symbols-outlined">save</mat-icon>
      Save Week
    </button>
    <button 
      mat-stroked-button 
      [disabled]="hasExcessHours() || isReadonly" (click)="submitWeek()"
    >
      <mat-icon class="material-symbols-outlined">send</mat-icon>
      Submit Week
    </button>
  </section>

  <!-- DATE RANGE CONTROLS -->
  <section role="region" aria-label="Date range controls" class="controls margin-sm-bottom">
    <h2 class="visually-hidden">Date range controls</h2>
    <div class="user-controls">
      <div class="left-controls">
          <button 
            mat-button 
            (click)="nextPrevClick(-1)"
            aria-label="previous week">
            <mat-icon class="material-symbols-outlined">chevron_left</mat-icon>
            Previous Week
          </button>

          <mat-form-field appearance="fill" class="range-picker-field">
            <mat-label for="dateRangePicker">Date Range</mat-label>
            <mat-date-range-input 
              id="dateRangePicker" 
              [rangePicker]="picker" 
              [formGroup]="rangeForm"
              (click)="picker.open()"
            >
              <mat-label for="startDate">startDate</mat-label>
              <input 
                id="startDate" 
                matStartDate 
                aria-labelledby="Start Date"
                placeholder="Start date" 
                formControlName="start" 
                readonly
              >
              <mat-label for="endDate">endDate</mat-label>
              <input 
                id="endDate" 
                matEndDate 
                aria-labelledby="End Date"
                placeholder="End date" 
                formControlName="end" 
                readonly
              >
            </mat-date-range-input>
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-date-range-picker #picker startView="year"></mat-date-range-picker>
          </mat-form-field>

          <button mat-button (click)="nextPrevClick(1)" aria-label="next week">
            Next Week
            <mat-icon class="material-symbols-outlined" iconPositionEnd>chevron_right</mat-icon>
          </button>
          <button mat-stroked-button aria-label="calendar icon" (click)="thisWeekClick()">
            <mat-icon class="material-symbols-outlined">calendar_today</mat-icon>
            Today
          </button>
      </div>
      <div class="right-controls">
        <p><mat-slide-toggle aria-label="Toggle the view for Weekend Columns" [checked]='!uiPreference?.timesheet?.daysToHide?.length' (change)="setPrefDisplayDays(['sat', 'sun'], $event.checked)">Sat / Sun</mat-slide-toggle></p>
      </div>
    </div>
    <div *ngIf="timesheetBacklog.length > 0" class="unsubmitted-weeks">
      <span>Backlog:</span>
      @let currentWeekStart = selectedWeek.start | date: 'yyyy-MM-dd';
      <mat-chip-set aria-label="Unsubmitted timesheets">
        @for (entry of timesheetBacklog; track entry[0]) {
          @let weekStart = entry[0];
          @let status = entry[1];
          @let weekof = weekStart | date: 'yyyy-MM-dd';
          <mat-chip [class]="'backlog-week status-' + entry[1]" [title]="getTimesheetStatusSummary(weekStart, status)"
            [class.is-current]="currentWeekStart == weekof" 
            (click)="setWeek(weekof!)">
            @if (isThisWeek(weekStart)) {
              This week:&nbsp;
            } @else {
              {{ weekStart | date: 'MM/dd' }}:&nbsp;
            }

            @if (status == 'Unsubmitted') {
              <mat-icon>more_horiz</mat-icon>
            } @else if (status == 'Review') {
              <mat-icon>comment</mat-icon>
            } @else if (status == 'Not created') {
              <mat-icon class="material-symbols-outlined">check_indeterminate_small</mat-icon>
            } @else if (status == 'Approved') {
              <mat-icon>done_all</mat-icon>
            } @else if (status == 'Submitted') {
              <mat-icon>check</mat-icon>
            }
          </mat-chip>
        }
      </mat-chip-set>
    </div>
  </section>
  
  <!-- TIMESHEET APPROVAL MESSAGE -->
  <mat-card 
    appearance="outlined"
    *ngIf="isApproved || isForReview || isSubmitted"
    class="timesheet-status warning-msg"
    [class.is-approved]="isApproved"
  >
    <mat-card-content>
      <p class="margin-top-none">
        Your timesheet for this week has been
        @if (isApproved) {
          <span class="is-approved">approved.</span>
        } @else if (isForReview) {
          <span class="is-review review-legend">returned for review.</span> 
          Please review the comments as well as any marked times and resubmit.
        } @else {
          <span class="is-submitted">submitted.</span>
        }
      </p>
      <p *ngIf="isForReview">
        Supervisor comments:
        @if (reviewerComments) {
          <b>"{{ reviewerComments }}"</b>
        } @else {
          <b>[None provided - please contact your supervisor if you have any questions.]</b>
        }
      </p>
    </mat-card-content>
  </mat-card>
</div>

<!-- TIMESHEET TABLE -->
<section role="region" aria-label="Timesheet table entries" class="table-container timesheet-table">
  <h2 class="visually-hidden">Timesheet table entries</h2>
  <table mat-table [dataSource]="projects" multiTemplateDataRows [trackBy]="projectTrackBy">
    <colgroup>
      <col />
      <col class="same-width-cols" span="{{weekDays.length}}"/>
    </colgroup>
    <ng-container matColumnDef="project">
      <th mat-header-cell *matHeaderCellDef class="project-name-cell" scope="col">
        <div class="title-holder">
          <b>Effort</b> &gt; Project
        </div>
      </th>
      <td 
        mat-cell 
        *matCellDef="let project" 
        class="project-name-cell" 
        (keydown.p)="togglePinProject(project)" 
        tabindex="0" 
        [class.accessible-tools]="uiPreference?.accessibility?.iconBtnPersistence == 'persistent'" 
        (keydown)="handleNavigationKeydown($event)"
      >
        <div 
          class="title-holder" 
          [class]="{ 'removed-title' : project.ui.isLegacy }"
        >
          <!-- push pin -->
          <div class="row-tools-front" *ngIf="! isReadonly">
            @if (project?.ui.groupName == "Pinned") {
              <button mat-icon-button 
                    (click)="togglePinProject(project)" 
                    class="is-pinned"
                    title="Unpin this to return it to the default location within your timesheet.">
                    <mat-icon>push_pin</mat-icon>
              </button>
            } @else {
              <button mat-icon-button  
                    (click)="togglePinProject(project)"
                    title="Pin this project to the top of your timesheet.">
                <mat-icon>push_pin</mat-icon>
              </button>
            }
          </div>
          <span title="Press 'p' to pin or unpin this project, or hover over it and click the pin icon." >
            @if (project?.ui.isVirtual && project?.ui.groupName !== "Pinned" && isGrouped(project)) {
              <span [class]="{ 'virtual-title' : !isReadonly }">
                @if (isReadonly) {
                  {{ project.title }}&nbsp;::&nbsp;
                }
                {{ project.ui.virtualTitle }}
              </span>
            } @else if (!isPrivilegedUser) {
              <span>{{ project.title }}</span>
            } @else {
              <a 
                [routerLink]="['/projects']" 
                [queryParams]="{ 'filter.key': project.key }">
                {{ project.title }}
              </a>
            }

            @if (showTimeType(project)) {
              <span class="single-time-type">&nbsp;::&nbsp;{{ project.ui.virtualTitle }}</span>
            }

            @if (project?.ui.groupName == "Pinned") {
              &nbsp;<span class="pinned-effort-hint">({{ project.effort.title }})</span>
            }
          </span>
          @if (project.ui.groupName == 'Pinned' && isOfficialLeaveCell(project, weekDays[weekDays.length - 1])) {
            <span class="imported-leave-badge" tabindex="0"
              [matTooltip]="officialLeaveHelp">
              <mat-icon aria-hidden="true">lock</mat-icon>Imported {{ officialLeaveUnavailable ? '(unavailable)' : '' }}
            </span>
          }
          @if (!! project?.description?.trim()) {
            <mat-icon [title]="project?.description?.trim()" class="project-description">info_outlined</mat-icon>
          }
          <!-- cut/copy/paste -->
          <div class="row-tools" *ngIf="! isReadonly && (uiPreference?.timesheet?.enableRowTools ?? true)">
            <button 
              mat-icon-button 
              (click)="clipboard.cutRow(project)"
              title="Cut row hours">
              <mat-icon>content_cut</mat-icon>
            </button>
            <button 
              mat-icon-button 
              (click)="clipboard.copyRow(project)"
              title="Copy row hours">
              <mat-icon>content_copy</mat-icon>
            </button>
            <button 
              mat-icon-button 
              (click)="clipboard.pasteRow(project)" 
              title="Paste row hours"
              [disabled]="clipboard.rowClipboard.length == 0">
              <mat-icon>content_paste</mat-icon>
            </button>
          </div>
        </div>
      </td>
      <td mat-footer-cell *matFooterCellDef class="project-total-cell">Daily subtotals</td>
    </ng-container>

    <ng-container matColumnDef="project-with-types-header">
      <td mat-cell *matCellDef="let project" (keydown)="handleNavigationKeydown($event)">
        <div class="project-with-types">
          <button mat-icon-button aria-label="expand and collapse" class="expando-collapso" title="Click to expand/collapse this project" [disabled]="isReadonly">
            <mat-icon *ngIf="isExpandedTypes(project); else open">expand_circle_down</mat-icon>
            <ng-template #open><mat-icon class="material-symbols-outlined">expand_circle_right</mat-icon>
            </ng-template>
          </button>
          <span *ngIf="!isPrivilegedUser; else projLink">{{ project.title }}</span>
          <ng-template #projLink>
            <a [routerLink]="['/projects']" [queryParams]="{ 'filter.key': project.key }">{{ project.title }}</a>
          </ng-template>
        </div>
      </td>
    </ng-container>

    <ng-container *ngFor="let day of weekDays; let colIndex = index" [matColumnDef]="day">
      <th 
        mat-header-cell 
        *matHeaderCellDef 
        class="time-input-header-cell" 
        scope="col" 
        [class]="'day day' + dayOfWeek(day)" 
        [class.is-today]="isToday(day)"
        [class.is-office-closed]="officeClosedHoliday(day)"
        [class.accessible-tools]="uiPreference?.accessibility?.iconBtnPersistence == 'persistent'"
      >
        <div class="col-title-holder">
          <div class="col-tools" *ngIf="! isReadonly && (uiPreference?.timesheet?.enableColTools ?? true)">
            <button mat-icon-button (click)="clipboard.cutCol(day)"
              title="Cut this day's hours"><mat-icon>content_cut</mat-icon></button>
            <button mat-icon-button (click)="clipboard.copyCol(day)"
              title="Copy this day's hours"><mat-icon>content_copy</mat-icon></button>
            <button mat-icon-button (click)="clipboard.pasteCol(day)" title="Paste this day's hours"
              [disabled]="clipboard.colClipboard.length == 0"><mat-icon>content_paste</mat-icon></button>
          </div>
          <span class="day-label" [@fadeAnimation]>{{ day | date: 'EEE, M/d' }}</span>
        </div>
      </th>
      <td 
        mat-cell 
        *matCellDef="let project; let rowIndex = dataIndex" 
        class="time-input-cell" 
        [class.has-notes]="!!project.notes[day] && !isReadonly"
        [class.is-review]="project?.review[day]?.isPleaseReview"
        [title]="project?.review[day]?.isPleaseReview ? 'Your supervisor has marked this time as needing review.' : ''"
        [class.is-today]="isToday(day)"
        [class.is-office-closed]="officeClosedHoliday(day)"
        [class]="'day' + dayOfWeek(day)"
      >
        <div [@fadeAnimation] *ngIf="!isReadonly || isOfficialLeaveCell(project, day); else hoursDisplayOnly" class="hours-editor-container" (keydown)="handleNavigationKeydown($event)">
          <mat-form-field 
            appearance="outline" 
            class="time-input" 
            [class.official-leave-input]="isOfficialLeaveCell(project, day)"
            [class]="'input-' + project.id + '-' + day"
            (contextmenu)="displayContextMenu($event, project, day)"
            [title]="isOfficialLeaveCell(project, day) ? '' : 'Select the cell and enter hours or press t to start a timer, or n to open notes.'"
          >
            <label 
              [attr.for]="'timeInput' + project.id + '-' + day + '-' + project.ui.timeTypeId" 
              class="visually-hidden">
              {{ project.title + ' ' + day }}
            </label>
            <input 
              [id]="'timeInput' + project.id + '-' + day + '-' + project.ui.timeTypeId"
              matInput 
              numeric 
              [readonly]="isReadonly || isOfficialLeaveCell(project, day)"
              [(ngModel)]="project.hours[day]" 
              (keypress)="handleHoursKeydown($event, project, day)"
              (change)="checkForAutoSave()"
            >
          </mat-form-field>
          @if (!isOfficialLeaveCell(project, day) && uiPreference?.timesheet?.tsCellToolLocation == 'persistent-right') {
          <div class="cell-tools-static">
            <button mat-icon-button (click)="startTimer(project, day)"><mat-icon>timer</mat-icon></button>
            <button mat-icon-button (click)="openNotesDialog(project, day)"><mat-icon>note</mat-icon></button>
          </div>
          } @else if (!isOfficialLeaveCell(project, day) && uiPreference?.timesheet?.tsCellToolLocation == 'active-bottom') {
          <div class="cell-tools">
            <button mat-icon-button (click)="startTimer(project, day)"><mat-icon>timer</mat-icon></button>
            <button mat-icon-button (click)="openNotesDialog(project, day)"><mat-icon [class.has-notes]="!!project.notes[day]">note</mat-icon></button>
          </div>
          }
        </div>

        <ng-template #hoursDisplayOnly>
          <div [@fadeAnimation] class="show-time-only">
            {{ project.hours[day] }} &nbsp;
            <button mat-icon-button *ngIf="project.notes[day]" [title]="'Notes for this day: &quot;' + project.notes[day] + '&quot;'"
              class="notes" (click)="openNotesDialog(project, day, true)"><mat-icon [class.has-notes]="!!project.notes[day]">notes</mat-icon></button>
          </div>
        </ng-template>
      </td>
      <td mat-footer-cell *matFooterCellDef class="total-cell"
        [ngClass]="{ 'excess-hours' : getTotalHoursFloat(day) > 24 }"> {{ getTotalHours(day) }} <span
          *ngIf="getTotalHoursFloat(day) > 24">&gt; 24</span></td>
    </ng-container>

    <ng-container matColumnDef="groupName">
      <td mat-cell *matCellDef="let project" colspan="1" class="group-name" (keydown)="handleNavigationKeydown($event)" [class.fmla]="project.effort.key=='fmla-leave'">
        <div class="title-holder">
          <div>
            @let expanded = isExpanded(project, true);
            <button 
              mat-icon-button 
              aria-label="expand and collapse" 
              [title]="`Click to ${expanded ? 'collapse' : 'expand'} this Effort`"
              [disabled]="isReadonly"
              class="expando-collapso">
              <mat-icon class="expand-icon" *ngIf="expanded; else open">expand_circle_down</mat-icon>
              <ng-template #open>
                <mat-icon class="material-symbols-outlined expand-icon">expand_circle_right</mat-icon>
              </ng-template>
            </button>
            @if (project.effort.isHR && project.effort.key != 'its-general-efforts') {
            <img src="/assets/images/time-off-two-color.svg" title="This is a Leave category." class="leave-image" />
            }
            <span *ngIf="!isPrivilegedUser; else effortLink">{{ project.ui.groupName || "Uncategorized" }}</span>
            <ng-template #effortLink>
              <a [routerLink]="['/efforts']" [queryParams]="{ 'filter.key': project.effort.key }">{{
                project.ui.groupName || "Uncategorized" }}</a>
            </ng-template>
            @if (isOfficialLeaveCell(project, weekDays[weekDays.length - 1])) {
              <span class="imported-leave-badge"><mat-icon aria-hidden="true">lock</mat-icon>Imported</span>
            }
          </div>
        </div>
        @if (isOfficialLeaveCell(project, weekDays[weekDays.length - 1])) {
          <p class="imported-leave-description">
            Official leave
            @if (officialLeaveUnavailable) { <strong>— Unavailable; reload to try again</strong> }
            · Read-only; edit in TAA. Updated when this timesheet loads.
          </p>
        }
        <div class="fmla-warning" *ngIf="project.effort.key=='fmla-leave' && !fmlaDismissed">
          This is a special leave category. Please only use after discussing it with your HR representative.
          <a href="#" (click)="dismissFmlaWarning()">Dismiss</a>
        </div>
      </td>
    </ng-container>
    <ng-container matColumnDef="pinned">
      <th mat-header-cell *matHeaderCellDef class="pinned-header">
        <div><mat-icon>push_pin</mat-icon> Pinned</div>
      </th>
      <td mat-cell *matCellDef="let project" class="pinned-header">
        <div><mat-icon>push_pin</mat-icon> Pinned</div>
      </td>
    </ng-container>

    <ng-container matColumnDef="empty">
      <th mat-header-cell *matHeaderCellDef></th>
      <td mat-cell *matCellDef="let project"></td>
    </ng-container>

    <ng-container *ngFor="let day of weekDays; let colIndex = index" [matColumnDef]="'empty-effort-header-' + day">
      <td mat-cell *matCellDef="let project" class="time-input-cell">
        @if (!isExpanded(project, true)) {
          <div [@fadeAnimation]>{{ getEffortDayTotal(project, day) }}</div>
        }
      </td>
    </ng-container>

    <ng-container *ngFor="let day of weekDays; let colIndex = index" [matColumnDef]="'empty-proj-type-header-' + day">
      <td mat-cell *matCellDef="let project" class="time-input-cell">
        @if (!isExpanded(project, false)) {
          <div [@fadeAnimation]>{{ getProjectTypeDayTotal(project, day) }}</div>
        }
      </td>
    </ng-container>

    <ng-container matColumnDef="totals">
      <th mat-header-cell *matHeaderCellDef class="time-input-header-cell totals-cell" scope="col">Totals</th>
      <td mat-cell *matCellDef="let project" class="time-input-cell totals-cell">
        <div [@fadeAnimation] [innerHTML]="rowTotal(project) | hours"></div>
      </td>
      <td mat-footer-cell *matFooterCellDef>
        <div class="time-input-cell">Total = <span [innerHTML]="total() | hours"></span>
        </div>
      </td>
    </ng-container>

    <ng-container matColumnDef="group-total">
      <td mat-cell *matCellDef="let project" class="time-input-cell totals-cell">
        <div [@fadeAnimation] [innerHTML]="rowGroupTotal(project) | hours" *ngIf="!isExpanded(project, true)"></div>
      </td>
    </ng-container>

    <ng-container matColumnDef="project-total">
      <td mat-cell *matCellDef="let project" class="time-input-cell totals-cell">
        <div [@fadeAnimation] [innerHTML]="rowProjectTotal(project) | hours" *ngIf="!isExpanded(project, false)"></div>
      </td>
    </ng-container>

    <ng-container matColumnDef="header-subtotals-label">
      <td mat-header-cell *matHeaderCellDef class="project-total-cell">Daily subtotals</td>
    </ng-container>

    <ng-container matColumnDef="header-holidays-label">
      <td mat-header-cell *matHeaderCellDef class="holiday-row-edge-cell"></td>
    </ng-container>

    <ng-container [matColumnDef]="'holiday-' + day" *ngFor="let day of weekDays; let colIndex = index">
      <th
        mat-header-cell
        *matHeaderCellDef
        class="holiday-header-cell"
        scope="col"
        [class.is-office-closed]="officeClosedHoliday(day)"
        [title]="officeClosedHoliday(day) ? officeClosedHoliday(day)?.fullName + ' - offices closed' : ''"
      >
        @if (officeClosedHoliday(day); as holiday) {
          <span class="holiday-label">
            <mat-icon class="material-symbols-outlined">beach_access</mat-icon>
            {{ holiday.label }}
          </span>
        }
      </th>
    </ng-container>

    <ng-container matColumnDef="header-holidays-total">
      <td mat-header-cell *matHeaderCellDef class="holiday-row-edge-cell"></td>
    </ng-container>

    <ng-container [matColumnDef]="'subtotals-' + day" *ngFor="let day of weekDays; let colIndex = index">
      <th mat-header-cell *matHeaderCellDef class="total-cell" scope="col"
        [ngClass]="{ 'excess-hours' : getTotalHoursFloat(day) > 24 }"> {{ getTotalHours(day) }}
        <span *ngIf="getTotalHoursFloat(day) > 24">&gt; 24</span>
      </th>
    </ng-container>

    <ng-container matColumnDef="header-total">
      <td mat-header-cell *matHeaderCellDef>
        <div class="time-input-cell">
          Total = <span [innerHTML]="total() | hours"></span>
        </div>
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true">
    </tr>

    <tr mat-header-row *matHeaderRowDef="displayedHolidayColumns; sticky: true" class="holiday-row" [hidden]="!hasOfficeClosedHolidays">
    </tr>

    <tr mat-header-row *matHeaderRowDef="displayedTotalsColumns; sticky: true" class="totals-row project-row header-totals-row">
    </tr>

    <tr mat-header-row *matHeaderRowDef="displayedPinnedHeaderRow" [hidden]="! anyPinned()" class="effort-header pinned-header-row"></tr>

    <tr mat-row *matRowDef="let row; 
      columns: displayedGroupHeaderRow; 
      when: isNextGroup;" class="effort-header" (click)="toggleEffort(row)">
    </tr>

    <tr mat-row *matRowDef="let row; 
      columns: displayedProjWithTypesRow; 
      when: isNextProjectWithTypes;" (click)="toggleProjectTypes(row)" class="project-row project-with-subtypes" [class.hidden-row]="!isExpanded(row, true)" [inert]="!isExpanded(row, true)">
    </tr>

    <tr mat-row *matRowDef="let row; 
      columns: displayedColumns;" class="project-row" [class]="{ 'pinned-row': row.ui.groupName == 'Pinned'}"
      [hidden]="isReadonly && !projectHasHours(row) && !row.ui.isLegacy && !isOfficialLeaveCell(row, weekDays[weekDays.length - 1])" [class.hidden-row]="!isExpanded(row, false)" [inert]="!isExpanded(row, false)">
    </tr>

    <tr mat-footer-row *matFooterRowDef="displayedColumns" class="totals-row">
    </tr>
  </table>
  
  <app-cell-context-menu 
    *ngIf="rightClickMenuItems.length > 0 && isDisplayContextMenu" 
    [ngStyle]="getRightClickMenuStyle()"
    [contextMenuItems]="rightClickMenuItems" 
    (onContextMenuItemClick)="handleMenuItemClick($event)">
  </app-cell-context-menu>

  <div class="loading-data" [class.showing]="isLoading | async">
    <mat-spinner></mat-spinner>
  </div>
</section>
